import { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  orderBy, 
  query, 
  serverTimestamp,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase.config.js';

export const useChat = (user, currentAgency, selectedChannel) => {
  const [messages, setMessages] = useState([]);
  const [pendingMessages, setPendingMessages] = useState([]); // Instant local messages
  const [loading, setLoading] = useState(true);
  const [userReadStatus, setUserReadStatus] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef({});

  // Écouter les messages
  useEffect(() => {
    if (!currentAgency?.key || !selectedChannel) return;

    const messagesQuery = query(
      collection(db, 'agencies', currentAgency.key, 'channels', selectedChannel, 'messages'),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMessages(messagesList);
      
      // Remove pending messages that now exist in Firebase
      if (user?.uid) {
        setPendingMessages(prev => 
          prev.filter(pending => {
            // Check if this exact message exists in Firebase now
            const exists = messagesList.some(real => 
              real.uid === user.uid && 
              real.text.trim() === pending.text.trim()
            );
            return !exists;
          })
        );
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentAgency?.key, selectedChannel, user?.uid]);

  // Écouter les statuts de lecture
  useEffect(() => {
    if (!currentAgency?.key || !selectedChannel) return;

    const userReadStatusQuery = query(
      collection(db, 'agencies', currentAgency.key, 'channels', selectedChannel, 'user_read_status'),
      orderBy('readAt', 'desc')
    );

    const unsubscribe = onSnapshot(userReadStatusQuery, (snapshot) => {
      const readStatusMap = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        readStatusMap[data.userId] = {
          ...data,
          id: doc.id
        };
      });
      setUserReadStatus(readStatusMap);
    });

    return () => unsubscribe();
  }, [currentAgency?.key, selectedChannel]);

  // Écouter les utilisateurs qui tapent
  useEffect(() => {
    if (!currentAgency?.key || !selectedChannel) return;

    const typingQuery = query(
      collection(db, 'agencies', currentAgency.key, 'channels', selectedChannel, 'typing'),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(typingQuery, (snapshot) => {
      const typingData = {};
      const now = Date.now();
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const timestamp = data.timestamp;
        let timeDiff = now;
        
        if (timestamp && typeof timestamp.toMillis === 'function') {
          timeDiff = now - timestamp.toMillis();
        } else if (timestamp && timestamp.seconds) {
          timeDiff = now - (timestamp.seconds * 1000);
        }
        
        if (timeDiff < 3000 && data.userId !== user?.uid) {
          typingData[data.userId] = {
            displayName: data.displayName,
            photoURL: data.photoURL
          };
        }
      });
      
      setTypingUsers(typingData);
    });

    return () => unsubscribe();
  }, [currentAgency?.key, selectedChannel, user?.uid]);

  const sendMessage = async (text) => {
    if (!text.trim() || !user || !currentAgency?.key || !selectedChannel) return;

    const messageText = text.trim();
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    
    // INSTANT: Add to pending messages immediately (WhatsApp style)
    const pendingMessage = {
      id: tempId,
      text: messageText,
      timestamp: new Date(),
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || user.email,
      photoURL: user.photoURL || null,
      isPending: true
    };
    
    // Show message INSTANTLY
    setPendingMessages(prev => [...prev, pendingMessage]);

    try {
      setIsTyping(false);
      await stopTyping();
      
      // Send to Firebase in background
      await addDoc(collection(db, 'agencies', currentAgency.key, 'channels', selectedChannel, 'messages'), {
        text: messageText,
        timestamp: serverTimestamp(),
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email,
        photoURL: user.photoURL || null
      });
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove pending message on error
      setPendingMessages(prev => prev.filter(msg => msg.id !== tempId));
    }
  };

  const startTyping = async () => {
    if (!user || !currentAgency?.key || !selectedChannel || isTyping) return;
    
    setIsTyping(true);
    
    try {
      await setDoc(
        doc(db, 'agencies', currentAgency.key, 'channels', selectedChannel, 'typing', user.uid),
        {
          userId: user.uid,
          displayName: user.displayName || user.email,
          photoURL: user.photoURL || null,
          timestamp: serverTimestamp()
        }
      );
    } catch (error) {
      console.error('Error updating typing status:', error);
    }
  };

  const stopTyping = async () => {
    if (!user || !currentAgency?.key || !selectedChannel) return;
    
    setIsTyping(false);
    
    try {
      await setDoc(
        doc(db, 'agencies', currentAgency.key, 'channels', selectedChannel, 'typing', user.uid),
        {
          userId: user.uid,
          displayName: user.displayName || user.email,
          photoURL: user.photoURL || null,
          timestamp: new Date(Date.now() - 10000)
        }
      );
    } catch (error) {
      console.error('Error stopping typing status:', error);
    }
  };

  // Ajouter une réaction
  const addReaction = async (messageId, emoji) => {
    if (!user || !currentAgency?.key || !selectedChannel) return;

    try {
      const messageRef = doc(db, 'agencies', currentAgency.key, 'channels', selectedChannel, 'messages', messageId);
      const userReaction = {
        userId: user.uid,
        displayName: user.displayName || user.email,
        photoURL: user.photoURL || null
      };

      await updateDoc(messageRef, {
        [`reactions.${emoji}`]: arrayUnion(userReaction)
      });
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  // Supprimer une réaction
  const removeReaction = async (messageId, emoji) => {
    if (!user || !currentAgency?.key || !selectedChannel) return;

    try {
      const messageRef = doc(db, 'agencies', currentAgency.key, 'channels', selectedChannel, 'messages', messageId);
      const userReaction = {
        userId: user.uid,
        displayName: user.displayName || user.email,
        photoURL: user.photoURL || null
      };

      await updateDoc(messageRef, {
        [`reactions.${emoji}`]: arrayRemove(userReaction)
      });
    } catch (error) {
      console.error('Error removing reaction:', error);
    }
  };

  // Combine Firebase messages with pending messages for instant display
  const allMessages = [...messages, ...pendingMessages];
  

  return {
    messages: allMessages,
    loading,
    userReadStatus,
    typingUsers,
    isTyping,
    sendMessage,
    startTyping,
    stopTyping,
    addReaction,
    removeReaction,
    typingTimeoutRef
  };
};